/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withMyTheme, SMALL_ROUNDED_CORNER } from "../../theme/theme";
import { css } from "@emotion/react";
import { isMobile, mobileCss } from "../../theme/isMobile";
import { MyButton } from "../../components/button/MyButton";
import { ArrowBack, ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { OfferHelmet } from '../../../helmet/OfferHelmet';
import offersConfig from './../../../offersConfig/offersConfig.json';
import { OfferDto } from './OfferData';

const IMAGE_HEIGHT = isMobile() ? '60vh' : '80vh';
const MOBILE_IMAGE_WIDTH = '90vw'

const OfferDetailsContainerStyle = withMyTheme((theme) => css`
    min-height: 100vh;
    height: 100vh;
    background-color: ${theme.palette.background.default};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5vw;
    padding: 0 7vw;
    overflow: hidden;
    
    ${mobileCss(`
        flex-direction: column;
        padding: 100px 0px 40px 0px;
        gap: 30px;
        height: auto;
        overflow: visible;
    `)}
`)

const ContentContainerStyle = css`
    flex: 5;
    display: flex;
    flex-direction: column;
    margin-top: 5vh;
    height: 90vh;
    align-self: flex-end;
    ${mobileCss(`
        margin-top: 2vh;
        max-width: 100vw;
        text-align: center;
        margin: 0 20px 0 20px;
        height: auto;
    `)}
`;

const OfferTitleStyle = withMyTheme((theme) => css`
    font-size: 2.5rem;
    font-weight: 600;
    color: ${theme.palette.text.primary};
    margin-bottom: 30px;
    font-family: ${theme.typography.h1.fontFamily};
    
    ${mobileCss(`
        font-size: 2rem;
        margin-bottom: 20px;
    `)}
`)

const DescriptionContainerStyle = withMyTheme((theme) => css`
    flex: 1;
    overflow-y: auto;
    padding-right: 16px;
    padding-bottom: 5vh;
    font-family: ${theme.typography.body1.fontFamily};
     overflow-y: scroll; 
    -ms-overflow-style: none;
    scrollbar-width: none;
    ::-webkit-scrollbar {
        display: none; 
    }
    
    ${mobileCss(`
        overflow-y: visible;
        padding-right: 0;
        max-height: none;
    `)}
`);

const OfferDescriptionStyle = withMyTheme((theme) => css`
    color: ${theme.palette.text.primary};
    font-size: 1.1rem;
    line-height: 1.8;
    white-space: pre-line;
    font-family: ${theme.typography.body1.fontFamily};
    
    & p, & ul, & ol {
        margin-bottom: 1rem;
    }
    
    & ul, & ol {
        padding-left: 1.5rem;
    }
    
    & a {
        color: ${theme.palette.primary.main};
        text-decoration: none;
        
        &:hover {
            text-decoration: underline;
        }
    }
    
    ${mobileCss(`
        font-size: 1rem;
        line-height: 1.6;
    `)}
`)

const ImageContainerStyle = css`
    position: relative;
    flex: 4;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40vw;
    height: ${IMAGE_HEIGHT};
    
    ${mobileCss(`
        width: ${MOBILE_IMAGE_WIDTH};
        flex-direction: column-reverse;
        align-items: center;
        justify-content: center;
    `)}
`;

const CarouselContainerStyle = css`
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const SliderContainerStyle = css`
    position: relative;
    height: ${IMAGE_HEIGHT};
    width: 40vw;
    overflow: hidden;
    border-radius: ${SMALL_ROUNDED_CORNER};
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    
    ${mobileCss(`
        width: ${MOBILE_IMAGE_WIDTH};
    `)}
`;

const SliderStyle = css`
    display: flex;
    transition: transform 0.5s ease;
    height: ${IMAGE_HEIGHT};
    ${mobileCss(`
        width: auto;    
    `)}
`;

const SlideStyle = css`
    min-width: 100%;
    height: ${IMAGE_HEIGHT};
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const OfferImageStyle = css`
    width: 40vw;
    height: ${IMAGE_HEIGHT};
    object-fit: cover;
    ${mobileCss(`
        width: ${MOBILE_IMAGE_WIDTH};
    `)}
`;

const NavigationButtonStyle = withMyTheme((theme) => css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: background-color 0.3s ease;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.9);
    }
    
    &.prev {
        left: 16px;
    }
    
    &.next {
        right: 16px;
    }
    
    ${mobileCss(`
        width: 32px;
        height: 32px;
    `)}
`);

const PaginationContainerStyle = css`
    display: flex;
    justify-content: center;
    margin-top: 16px;
    gap: 8px;
`;

const PaginationDotStyle = withMyTheme((theme) => css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${theme.palette.divider};
    transition: background-color 0.3s ease, transform 0.3s ease;
    
    &.active {
        background-color: ${theme.palette.primary.main};
        transform: scale(1.2);
    }
`);

const BackButtonStyle = withMyTheme((theme) => css`
    position: fixed;
    top: 5vh;
    left: 5vw;
    background-color: ${theme.palette.primary.dark};
    
    ${mobileCss(`
        align-self: center;
    `)}
`);

const LoadingStyle = withMyTheme((theme) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    font-size: 1.5rem;
    color: ${theme.palette.text.secondary};
    font-family: ${theme.typography.body1.fontFamily};
`)

const ErrorStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 400px;
    font-size: 1.5rem;
    color: ${theme.palette.error.main};
    font-family: ${theme.typography.body1.fontFamily};
    gap: 2rem;
`)

const NotFoundStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 400px;
    font-size: 1.5rem;
    color: ${theme.palette.text.secondary};
    font-family: ${theme.typography.body1.fontFamily};
    gap: 2rem;
`)

interface ImageCarouselProps {
    images: { url: string; alt: string }[];
    getImageUrl: (url: string) => string;
}

const ImageCarousel = ({ images, getImageUrl }: ImageCarouselProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const sliderRef = useRef<HTMLDivElement>(null);

    const goToSlide = (index: number) => {
        if (index < 0) {
            setCurrentIndex(images.length - 1);
        } else if (index >= images.length) {
            setCurrentIndex(0);
        } else {
            setCurrentIndex(index);
        }
    };

    const goToPrevious = () => {
        goToSlide(currentIndex - 1);
    };

    const goToNext = () => {
        goToSlide(currentIndex + 1);
    };

    useEffect(() => {
        if (sliderRef.current) {
            sliderRef.current.style.transform = `translateX(-${currentIndex * 100}%)`;
        }
    }, [currentIndex]);

    return (
        <div css={CarouselContainerStyle}>
            <div css={SliderContainerStyle}>
                <div
                    css={SliderStyle}
                    ref={sliderRef}
                >
                    {images.map((image, index) => (
                        <div key={index} css={SlideStyle}>
                            <img
                                src={getImageUrl(image.url)}
                                alt={image.alt}
                                css={OfferImageStyle}
                            />
                        </div>
                    ))}
                </div>

                {images.length > 1 && (
                    <>
                        <div
                            css={NavigationButtonStyle}
                            className="prev"
                            onClick={goToPrevious}
                        >
                            <ArrowBackIosNew fontSize="small" />
                        </div>
                        <div
                            css={NavigationButtonStyle}
                            className="next"
                            onClick={goToNext}
                        >
                            <ArrowForwardIos fontSize="small" />
                        </div>
                    </>
                )}
            </div>

            {images.length > 1 && (
                <div css={PaginationContainerStyle}>
                    {images.map((_, index) => (
                        <div
                            key={index}
                            css={PaginationDotStyle}
                            className={index === currentIndex ? 'active' : ''}
                            onClick={() => goToSlide(index)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export const OfferDetails = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [offer, setOffer] = useState<OfferDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const setGeneralError = () => {
        setError("Nie udało się załadować oferty 😔. Spójrz na oferty na Booksy lub skontaktuj się ze mną.")
    }

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);

        const fetchOffer = async () => {
            if (!id) {
                setGeneralError()
                console.error(`No offer ID provided`)
                setLoading(false);
                return;
            }

            // Try to find the offer in the initialOffers first
            const findFallbackOffer = () => {
                const fallbackOffer = (offersConfig as OfferDto[]).find(offer => offer.slug === id || offer.id == parseInt(id, 10));
                if (fallbackOffer) {
                    console.log(`Found offer with ID ${id} in fallback data.`);
                    setOffer(fallbackOffer as OfferDto);
                    return true;
                }
                return false;
            };

            if (!findFallbackOffer()) {
                setGeneralError();
            }
            setLoading(false);
        };

        fetchOffer();
    }, [id]);

    const handleBackClick = () => {
        // Check if we can go back in history, otherwise go to home page
        if (window.history.length > 2) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    if (loading) {
        return (
            <div css={OfferDetailsContainerStyle}>
                <div css={LoadingStyle}>Loading offer details...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div css={OfferDetailsContainerStyle}>
                <div css={ErrorStyle}>
                    <div>{error}</div>
                    <MyButton
                        text="Powrót"
                        variant="contained"
                        colorVariant="primary"
                        startIcon={<ArrowBack />}
                        onClick={handleBackClick}
                    />
                </div>
            </div>
        );
    }

    if (!offer) {
        return (
            <div css={OfferDetailsContainerStyle}>
                <div css={NotFoundStyle}>
                    <div>Nie udało się załadować informacji o ofercie</div>
                    <MyButton
                        text="Powrót"
                        variant="contained"
                        colorVariant="primary"
                        startIcon={<ArrowBack />}
                        onClick={handleBackClick}
                    />
                </div>
            </div>
        );
    }

    const getImageUrl = (url: string) => {
        if (url.startsWith('http')) {
            return url;
        } else {
            return `../${url}`;
        }
    }

    const stripHtmlDom = (html: string) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || "";
      }

    return (
        <>
            <OfferHelmet
                offerName={offer.name}
                offerDescription={stripHtmlDom(offer.description)}
                offerImage={offer.main_image.url}
            />
            <div css={OfferDetailsContainerStyle}>
                <MyButton
                    text="Powrót"
                    variant="contained"
                    colorVariant="primary"
                    startIcon={<ArrowBack />}
                    onClick={handleBackClick}
                    additionalCss={BackButtonStyle}
                />
                <div css={ContentContainerStyle}>
                    <h2 css={OfferTitleStyle}>
                        {offer.details_name || offer.name}
                    </h2>
                    <div css={DescriptionContainerStyle}>
                        <div
                            css={OfferDescriptionStyle}
                            dangerouslySetInnerHTML={{ __html: offer.description }}
                        />
                    </div>
                </div>
                <div css={ImageContainerStyle}>
                    <ImageCarousel
                        images={[
                            offer.main_image,
                            ...(offer.additional_images || [])
                        ]}
                        getImageUrl={getImageUrl}
                    />
                </div>
            </div>
        </>
    );
}; 